// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

// HW.3 (COB-323): the hardware (Ledger) sign-in panel, rendered by both
// chooser entry points (home + shared-vault accept landing) when the user
// picks the 'hardware' method. Collects the account email and drives
// `session.loginWithHardware` from the connect click — the click IS the
// user gesture the WebHID permission prompt requires, so this component
// pre-warms the lazy device chunk on mount and the click path does no
// network work before the prompt.
//
// Deliberately free of device-stack imports: everything Ledger-side loads
// through session-context's dynamic import, keeping the main bundle
// ledger-free. Device errors are therefore discriminated by `name`, not
// `instanceof` (same pattern as the worker-client's reconstructError).

import { useEffect, useState } from 'preact/hooks';
import { useSession, EmailAlreadyRegisteredError } from '../session-context.js';
import { validateEmail } from '../auth-flow.js';
import '../styles/home.css';
import '../styles/accept.css';

/** True when this browser can talk WebHID (Chrome/Edge/Chromium). */
export function webHidSupported(): boolean {
  return typeof navigator !== 'undefined' && 'hid' in navigator;
}

/** The Ledger app name for the session's network. Mirrors the key-agent's
 *  `expectedDeviceApp(toKeyAgentNetwork(...))` — duplicated (2 lines, keep
 *  in lock-step) because importing it would drag the device stack into the
 *  main bundle. Unknown networks fail closed to mainnet, exactly like
 *  `toKeyAgentNetwork`. */
export function deviceAppLabel(bitcoinNetwork: string | null): string {
  return bitcoinNetwork === 'signet' ||
    bitcoinNetwork === 'testnet' ||
    bitcoinNetwork === 'regtest'
    ? 'Bitcoin Test'
    : 'Bitcoin';
}

/** Map a `loginWithHardware` failure to user-facing copy. Device-stack
 *  errors arrive name-tagged (`WrongDeviceAppError` carries actionable
 *  copy of its own; the transport errors need friendlier lines). */
export function hardwareLoginErrorMessage(err: unknown): string {
  if (err instanceof EmailAlreadyRegisteredError) return err.message;
  if (err instanceof Error) {
    switch (err.name) {
      case 'WrongDeviceAppError':
        return err.message; // already actionable (names the app to open)
      case 'TransportOpenUserCancelled':
        return 'No device was selected. Plug in your Ledger, unlock it, and try again.';
      case 'TransportInterfaceNotAvailable':
      case 'InvalidStateError':
        return 'Your Ledger appears to be in use by another app or tab (such as Ledger Live). Close it and try again.';
      default:
        return err.message || 'Could not connect to the hardware wallet.';
    }
  }
  return 'Could not connect to the hardware wallet.';
}

export function HardwareLogin({
  onBack,
  initialEmail,
}: {
  onBack: () => void;
  initialEmail?: string;
}) {
  const session = useSession();
  const [email, setEmail] = useState(initialEmail ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-warm the lazy device chunk so the connect click's WebHID prompt
  // stays inside the transient user-activation window even on a cold
  // cache. Best-effort: the click path performs the real import.
  useEffect(() => {
    import('../hardware-connect.js').catch(() => {
      /* non-fatal — the connect click retries the import */
    });
  }, []);

  const supported = webHidSupported();
  const appLabel = deviceAppLabel(session.bitcoinNetwork);
  const emailInvalid = validateEmail(email) !== null;

  async function handleConnect(e?: Event) {
    if (e) e.preventDefault();
    const emailError = validateEmail(email);
    if (emailError) {
      setError(emailError);
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await session.loginWithHardware(email);
      // Success unmounts this panel via the parent's isAuthenticated gate.
    } catch (err) {
      setError(hardwareLoginErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="login-chooser login-chooser--minimal">
      <h2 class="login-chooser-title">Hardware wallet</h2>
      <p class="accept-subtitle">
        Plug in your Ledger, unlock it with its PIN, and open the{' '}
        <strong>{appLabel}</strong> app. Your keys stay on the device — every
        vault transaction is confirmed on its screen.
      </p>

      {!supported && (
        <p class="offer-error">
          This browser can't talk to USB hardware wallets. Use Chrome, Edge,
          or another browser that supports WebHID.
        </p>
      )}

      <form onSubmit={handleConnect}>
        <div class="accept-field">
          <label class="accept-label">Email</label>
          <input
            class="accept-input"
            type="email"
            autocomplete="email"
            placeholder="you@example.com"
            value={email}
            disabled={busy}
            onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
          />
          <p class="accept-input-hint">
            Your account email. New here? This registers your account.
            Returning on this browser? Use the email you registered with.
          </p>
        </div>

        {error && <p class="offer-error">{error}</p>}

        <div class="accept-actions">
          <button
            type="button"
            class="btn btn-secondary"
            onClick={onBack}
            disabled={busy}
          >
            Back
          </button>
          <button
            type="submit"
            class="btn btn-primary"
            disabled={busy || !supported || emailInvalid}
            aria-busy={busy}
          >
            {busy ? 'Connecting…' : 'Connect device'}
          </button>
        </div>
      </form>
    </div>
  );
}
